import { Simplify, SetRequired } from 'type-fest'
import {
  Location,
  LocationAutocompleteResult,
  LocationSuggestion,
  Waypoint,
} from '@/types/trip'
import { FlightStatusProvider, TravelMode } from '@/utils/enums'

export const transportationTypes = [
  'driving',
  'transit',
  'flight',
  'cycling',
  'walking',
  'boat',
] as const
export type TransportationType = (typeof transportationTypes)[number]

export interface Option {
  uppercase: string
  minLength: number
  maxLength: number
}

export interface TransportationField {
  type: string
  identifier: string
  options: Option[]
  title: string
  onModel: string
}

export interface TransportationForm {
  type: string
  fields: TransportationField[]
  title: string
  highlight: string
}

export interface DateTime {
  data: string
  display: string
}

export interface Flight {
  airport: string
  value: string
  transportation: string
}

export interface Airport {
  code: string
  name: string
}

export interface AirportDetails extends Airport {
  location: {
    city: string
    country: string
    coordinates: [number, number] // [0] = latitude, [1] = longitude
  }
}

export interface FlightDetails {
  departureDateFlight: string
  arrivalDateFlight?: string
  departureAirport?: string
  arrivalAirport?: string
  flightNumber: string
}

export interface FlightDetailsAirport {
  iata: string
  location?: Location
}

export interface FlightDetailsNew {
  label: string
  airline?: string
  flightNumber: string
  departure: {
    date: string
    airport: FlightDetailsAirport
  }
  arrival: {
    date: string
    airport: FlightDetailsAirport
  }
}

export interface FlightStatusJuncture {
  airport: AirportDetails
  date: string
  gate?: string
  terminal?: string
}

export interface FlightStatus {
  status:
    | 'ACTIVE'
    | 'CANCELED'
    | 'DIVERTED'
    | 'LANDED'
    | 'NOT_OPERATIONAL'
    | 'REDIRECTED'
    | 'SCHEDULED'
    | 'UNKNOWN'
    | 'NOW_TRACKING'
  flightNumber: string
  departure: FlightStatusJuncture
  arrival: FlightStatusJuncture
  duration?: number
  isPreRegistered?: boolean
  isEligibleForCompensation?: boolean
}

export interface TransportationMetadata {
  raw: any
}

export interface FlightUpdateRule {
  booking?: string
  provider: FlightStatusProvider
  ruleId: string
  rawData: any
  active: boolean
  latestUpdate?: string
  sentReminders: number[]
}

export interface DateRange {
  start?: string
  end?: string
}

export interface RouteLocation {
  latitude: number
  longitude: number
}

export type RouteAvoidOption = 'tolls' | 'highways' | 'ferries'
export type RouteAvoidOptions = RouteAvoidOption[]

export interface Route {
  _id: string
  travelMode: TravelMode
  locations: RouteLocation[] // with atleast 2 locations
  polyline: string
  distance: number // Distance in meters
  center: { latitude: number; longitude: number }
  duration?: number // Duration in seconds
  avoidOptions?: RouteAvoidOptions
}

export type RouteSuggestion = Omit<Route, '_id'>

// Transportation Base  --------------------------------------------------------

export interface BaseTransportation {
  _id: string
  emoji: string
  // TODO: remove when legacy system is removed
  legacySystemId: string
  createdBy: string
  documents: string[]
  origin: Location
  destination: Location
  route?: Route
  label?: string
  period?: DateRange
  departureDate?: string
  arrivalDate?: string
  comment?: string
  isArrivalTransportation?: boolean
  groupId?: string
  title?: string // included in the trip list and show endpoint
}

/**
 * Base transportation type that is used to create a new transportation on the
 * server and shared between all transportation types.
 */
export interface BaseTransportationCreateInput
  extends Omit<
    BaseTransportation,
    | '_id'
    | 'legacySystemId'
    | 'createdBy'
    | 'route'
    | 'documents'
    | 'origin'
    | 'destination'
  > {
  documents: string[]
  origin: LocationSuggestion | Location
  destination: LocationSuggestion | Location
  route?: RouteSuggestion
}

/**
 * Base transportation type that is used to update a transportation on the
 * server and shared between all transportation types.
 */
export interface BaseTransportationUpdateInput {
  _id: string
  documents?: string[]
  emoji?: string
  origin?: LocationSuggestion | Location
  destination?: LocationSuggestion | Location
  route?: Route | RouteSuggestion | null
  label?: string | null
  period?: DateRange | null
  departureDate?: string | null
  arrivalDate?: string | null
  comment?: string | null
  bookingReference?: string | null
}

/**
 * Base transportation type that describes the input types from the UI and is
 * shared between all transportation types.
 */
export type BaseTransportationUIInput = Omit<
  BaseTransportationCreateInput,
  'origin' | 'destination'
> & {
  _id?: string
  legacySystemId?: string
  origin?: LocationAutocompleteResult
  destination?: LocationAutocompleteResult
}

type GetCreateInput<TTransportation> = Simplify<
  Omit<TTransportation, keyof BaseTransportation> &
    BaseTransportationCreateInput
>

/** Creates the update input type for the specific transportation type
 * This type will:
 * - Keep specific required fields still required
 * - Make optional fields also nullable
 */
type GetUpdateInput<TTransportation> = Simplify<
  BaseTransportationUpdateInput & {
    // Keep specific required fields still required
    [K in keyof Omit<
      Required<TTransportation>,
      keyof BaseTransportation
    > as K extends keyof TTransportation
      ? {} extends Pick<TTransportation, K>
        ? never
        : K
      : never]?: TTransportation[K]
  } & {
    // Make optional fields also nullable
    [K in keyof Omit<
      TTransportation,
      keyof BaseTransportation
    > as {} extends Pick<TTransportation, K> ? K : never]?:
      | TTransportation[K]
      | null
  }
>

type GetCreateUIInput<TTransportationCreateInput> = Simplify<
  Omit<TTransportationCreateInput, keyof BaseTransportationCreateInput> &
    BaseTransportationUIInput
>

// Transportation Driving  -----------------------------------------------------

export interface Rental {
  _id: string
  type: 'car' | 'camper' | 'other'
  period?: DateRange
  provider?: string
  bookingReference?: string
  pickUpLocation?: Location
  dropOffLocation?: Location
}

export interface CreateRentalInput {
  type: 'car' | 'camper' | 'other'
  period?: DateRange
  provider?: string
  bookingReference?: string
  pickUpLocation?: LocationSuggestion
  dropOffLocation?: LocationSuggestion
}

export interface DrivingTransportation extends BaseTransportation {
  type: 'driving'
  rentals: string[]
}

export type DrivingTransportationCreateInput =
  GetCreateInput<DrivingTransportation>

export type DrivingTransportationUpdateInput =
  GetUpdateInput<DrivingTransportation>

export type DrivingTransportationUIInput =
  GetCreateUIInput<DrivingTransportationCreateInput>

// Transportation Transit  -----------------------------------------------------

export interface TransitTransportation extends BaseTransportation {
  type: 'transit'
  provider?: string
  bookingReference?: string
  vehicleNumber?: string
  seatNumber?: string
  departurePlatform?: string
  arrivalPlatform?: string
}

export type TransitTransportationCreateInput =
  GetCreateInput<TransitTransportation>

export type TransitTransportationUpdateInput =
  GetUpdateInput<TransitTransportation>

export type TransitTransportationUIInput =
  GetCreateUIInput<TransitTransportationCreateInput>

// Transportation Flight  ------------------------------------------------------

export interface FlightTransportation extends BaseTransportation {
  type: 'flight'
  departureAirport?: { iata?: string }
  arrivalAirport?: { iata?: string }
  provider?: string
  bookingReference?: string
  flightNumber?: string
  seatNumber?: string
  flightUpdateRule?: FlightUpdateRule
  preRegistered?: boolean
  registrationError?: boolean
}

export type FlightTransportationCreateInput =
  GetCreateInput<FlightTransportation>

export type FlightTransportationUpdateInput =
  GetUpdateInput<FlightTransportation>

export type FlightTransportationUIInput = GetCreateUIInput<
  SetRequired<
    FlightTransportationCreateInput,
    'departureAirport' | 'arrivalAirport'
  >
>

// Transportation Cycling  -----------------------------------------------------

export interface CyclingTransportation extends BaseTransportation {
  type: 'cycling'
}

export type CyclingTransportationCreateInput =
  GetCreateInput<CyclingTransportation>

export type CyclingTransportationUpdateInput =
  GetUpdateInput<CyclingTransportation>

export type CyclingTransportationUIInput =
  GetCreateUIInput<CyclingTransportationCreateInput>

// Transportation Walking  -----------------------------------------------------

export interface WalkingTransportation extends BaseTransportation {
  type: 'walking'
}

export type WalkingTransportationCreateInput =
  GetCreateInput<WalkingTransportation>

export type WalkingTransportationUpdateInput =
  GetUpdateInput<WalkingTransportation>

export type WalkingTransportationUIInput =
  GetCreateUIInput<WalkingTransportationCreateInput>

// Transportation Boat  --------------------------------------------------------

export interface BoatTransportation extends BaseTransportation {
  type: 'boat'
  provider?: string
  bookingReference?: string
}

export type BoatTransportationCreateInput = GetCreateInput<BoatTransportation>

export type BoatTransportationUpdateInput = GetUpdateInput<BoatTransportation>

export type BoatTransportationUIInput =
  GetCreateUIInput<BoatTransportationCreateInput>

// Transportation  -------------------------------------------------------------

export type Transportation =
  | DrivingTransportation
  | TransitTransportation
  | FlightTransportation
  | CyclingTransportation
  | WalkingTransportation
  | BoatTransportation

export type TransportationCreateInput =
  | DrivingTransportationCreateInput
  | TransitTransportationCreateInput
  | FlightTransportationCreateInput
  | CyclingTransportationCreateInput
  | WalkingTransportationCreateInput
  | BoatTransportationCreateInput

export type TransportationUpdateInput =
  | DrivingTransportationUpdateInput
  | TransitTransportationUpdateInput
  | FlightTransportationUpdateInput
  | CyclingTransportationUpdateInput
  | WalkingTransportationUpdateInput
  | BoatTransportationUpdateInput

export type TransportationUIInput =
  | DrivingTransportationUIInput
  | TransitTransportationUIInput
  | FlightTransportationUIInput
  | CyclingTransportationUIInput
  | WalkingTransportationUIInput
  | BoatTransportationUIInput

/**
 * Transportation items grouped by origin and destination waypoints.
 */
export interface GroupedBooking {
  _id: string
  origin: Waypoint | null
  destination: Waypoint | null
  data: any[]
}

// Transportation Group  ------------------------------------------------------

export type TransportationGroupType = 'driving'

export interface TransportationGroupSharedProps {
  emoji: string
  label?: string
  rentals?: string[]
}

export interface TransportationGroup {
  _id: string
  tripId: string
  type: TransportationGroupType
  fromWaypointIds: string[]
  hasArrivalTransportation: boolean
  sharedProps: TransportationGroupSharedProps
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface TransportationGroupCreateInput {
  type: TransportationGroupType
  fromWaypointIds: string[]
  hasArrivalTransportation?: boolean
  originLocation?: LocationSuggestion | Location
  destinationLocation?: LocationSuggestion | Location
  seedTransportationId: string
}

export interface TransportationGroupUpdateInput {
  fromWaypointIds?: string[]
  hasArrivalTransportation?: boolean
  originLocation?: LocationSuggestion | Location
  destinationLocation?: LocationSuggestion | Location
  sharedProps?: Partial<TransportationGroupSharedProps>
}

export type TransportationGroupUpdateResult =
  | { kind: 'updated'; group: TransportationGroup }
  | { kind: 'demoted'; groupId: string }

/**
 * Suggestion the server returns (in the create-waypoint response) when a newly
 * inserted waypoint touches an existing driving group, prompting the UI to ask
 * whether the new leg should be added to that group.
 */
export interface TransportationGroupExtensionSuggestion {
  groupId: string
  insertedWaypointId: string
}

export function isPersistedUITransportation(
  transportation: TransportationUIInput,
): transportation is SetRequired<
  TransportationUIInput,
  '_id' | 'legacySystemId'
> {
  return !!transportation._id
}
